import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  useLogin,
  useSendPasswordResetEmail,
} from '@/features/AuthProvider/AuthProvider';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { Input } from '@/ui/Input/Input';
import { Navbar } from '@/ui/Navbar/Navbar';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { T } from '@/ui/Text/Text';

const StyledWrapper = styled(Flex)<{
  readonly renderedPage: 'login' | 'forgot';
}>`
  background: ${({ renderedPage }) =>
      renderedPage === 'login'
        ? "url('/img/login-bg.svg')"
        : "url('/img/forgot-password-bg.svg')"}
    no-repeat right center;
  background-size: auto 100%;
  height: calc(100vh - 72px);
  background-color: ${({ theme }) => theme.color('#E5E5E5')};
  width: 100%;
`;

const StyledCard = styled(Flex)`
  background: ${({ theme }) => theme.color('#FFFFFF')};
  border-radius: 4px;
  padding: 24px;
  max-width: 400px;
  width: 100%;
  min-height: 364px;
  margin: auto 16px;
`;

type Props = {
  readonly setCurrentView: (page: 'login' | 'forgot') => void;
  readonly email: string;
  readonly setEmail: (email: string) => void;
};

export const Login = ({
  setCurrentView: setRenderedPage,
  email,
  setEmail,
}: Props) => {
  const { login, isLoading } = useLogin();
  const [password, setPassword] = useState('');
  const toast = useToast();
  const intl = useIntl();
  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        if (!navigator.onLine) {
          return toast({
            subtitle: intl.formatMessage({
              defaultMessage: 'Please connect to the internet to log in.',
              id: '7cp5/a',
            }),
            mode: TOASTTYPE.No_Connection,
          });
        }
        try {
          await login({ email, password });
        } catch (err) {
          console.error('Something went wrong while logging in.', err);
        }
      }}
    >
      <Flex direction="column" gap={16}>
        <Flex justify="center" padding={20}>
          <T h1>
            <FormattedMessage defaultMessage="Sign in" id="SQJto2" />
          </T>
        </Flex>
        <Flex direction="column" justify="end">
          <Flex direction="column" gap={16}>
            <Input
              onChange={setEmail}
              placeholder="email"
              required
              type="email"
              value={email}
            />
            <Input
              onChange={setPassword}
              placeholder="password"
              required
              type="password"
              value={password}
            />
          </Flex>
          <Flex justify="center" padding="20px 0px">
            <Button
              mode="transparent"
              onClick={() => setRenderedPage('forgot')}
            >
              <T link>
                <FormattedMessage
                  defaultMessage="Forgot password?"
                  id="V/JHlm"
                />
              </T>
            </Button>
          </Flex>

          <Button loading={isLoading} mode="primary" size="large" type="submit">
            <FormattedMessage defaultMessage="Sign in" id="SQJto2" />
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export const ForgotPassword = ({
  setCurrentView: setRenderedPage,
  email,
  setEmail,
}: Props) => {
  const { isLoading, sendPasswordResetEmail } = useSendPasswordResetEmail();

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        sendPasswordResetEmail({ email });
      }}
    >
      <Flex direction="column" gap={16}>
        <Flex justify="center" padding={20}>
          <T h1>
            <FormattedMessage defaultMessage="Forgot password" id="9H3bhD" />
          </T>
        </Flex>
        <Flex direction="column" gap={16} grow>
          <Input
            onChange={setEmail}
            placeholder="email"
            required
            type="email"
            value={email}
          />
          <Button loading={isLoading} mode="primary" size="large" type="submit">
            <T>
              <FormattedMessage
                defaultMessage="Send instructions"
                id="A5ffnJ"
              />
            </T>
          </Button>

          <Flex justify="center" padding={8}>
            <Button mode="transparent" onClick={() => setRenderedPage('login')}>
              <T link>
                <FormattedMessage
                  defaultMessage="Back to sign in"
                  id="odTkf5"
                />
              </T>
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </form>
  );
};

export const AuthPage = () => {
  const [currentView, setCurrentView] = useState<'login' | 'forgot'>('login');
  const [email, setEmail] = useState('');

  return (
    <>
      <Navbar isAdmin={false} />
      <StyledWrapper align="center" justify="center" renderedPage={currentView}>
        <StyledCard direction="column" justify="space-between">
          {currentView === 'login' ? (
            <Login
              email={email}
              setCurrentView={setCurrentView}
              setEmail={setEmail}
            />
          ) : (
            <ForgotPassword
              email={email}
              setCurrentView={setCurrentView}
              setEmail={setEmail}
            />
          )}
        </StyledCard>
      </StyledWrapper>
    </>
  );
};
